import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import apiClient from "@/api/client";
import { DatePicker } from "@/components/ui/DatePicker";
import { useExecuteAronWrite } from "../api/aronApi";
import type { AronPendingWrite } from "../types";

interface NamedRow {
  id: string;
  name: string;
}

const PRIORITIES = [
  { value: "low", label: "Niski" },
  { value: "standard", label: "Standardowy" },
  { value: "high", label: "Wysoki" },
];

function useNamedList(path: string) {
  return useQuery<NamedRow[]>({
    queryKey: ["aron", "picklist", path],
    queryFn: async () => {
      const r = await apiClient.get<{ data: NamedRow[] }>(path);
      return (r.data.data ?? []).map((x) => ({ id: String(x.id), name: x.name }));
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Review modal for an ARON-proposed Requisition (Faza 3 write gate, per-object UX).
 * ARON only *proposes* the body; the user reviews/edits the real fields here and
 * confirms — the create then runs through the gated execute-write endpoint with
 * the edited payload. (One bespoke modal per object type, as planned.)
 */
export function AronRequisitionModal({
  conversationId,
  write,
  onClose,
}: {
  conversationId: string;
  write: AronPendingWrite;
  onClose: () => void;
}) {
  const body = (write.body ?? {}) as Record<string, unknown>;
  const projects = useNamedList("/projects/");
  const contractors = useNamedList("/contractors/");
  const execute = useExecuteAronWrite();

  const [project, setProject] = useState<string>(String(body.project ?? ""));
  const [contractor, setContractor] = useState<string>(String(body.contractor ?? ""));
  const [description, setDescription] = useState<string>(String(body.description ?? ""));
  const [priority, setPriority] = useState<string>(String(body.priority ?? "standard"));
  const [deadline, setDeadline] = useState<string>(String(body.deadline ?? ""));
  const [error, setError] = useState<string>("");

  // Line items proposed by ARON are passed through unchanged (reviewed here).
  const lineItems = Array.isArray(body.line_items) ? (body.line_items as Record<string, unknown>[]) : [];

  const valid = project && description.trim().length > 0;

  const submit = async () => {
    setError("");
    if (!valid) {
      setError("Wybierz przedsięwzięcie i podaj opis.");
      return;
    }
    const payload: Record<string, unknown> = {
      ...body,
      project,
      contractor: contractor || null,
      description: description.trim(),
      priority,
      deadline: deadline || null,
      line_items: lineItems,
    };
    try {
      const res = await execute.mutateAsync({ conversationId, writeId: write.id, body: payload });
      if (res.status === "failed") {
        setError(
          String(
            res.response_summary?.error_detail ||
              res.response_summary?.denied_reason ||
              "",
          ) || `Nie udało się utworzyć (kod ${res.response_status ?? "?"}).`,
        );
        return;
      }
      onClose();
    } catch {
      setError("Nie udało się utworzyć zapotrzebowania.");
    }
  };

  const projectName = useMemo(
    () => projects.data?.find((p) => p.id === project)?.name,
    [projects.data, project],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold">Nowe zapotrzebowanie — przejrzyj i utwórz</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 px-4 py-4 text-sm">
          <p className="rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            ARON zaproponował poniższe dane. Sprawdź i popraw — utworzenie nastąpi dopiero po kliknięciu „Utwórz".
          </p>

          <label className="block">
            <span className="text-xs text-gray-500">Przedsięwzięcie *</span>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800"
            >
              <option value="">— wybierz —</option>
              {projects.data?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {project && !projectName && !projects.isLoading && (
              <span className="text-[11px] text-amber-600">Zaproponowany identyfikator nie jest na liście — wybierz ręcznie.</span>
            )}
          </label>

          <label className="block">
            <span className="text-xs text-gray-500">Podwykonawca</span>
            <select
              value={contractor}
              onChange={(e) => setContractor(e.target.value)}
              className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800"
            >
              <option value="">— brak —</option>
              {contractors.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs text-gray-500">Opis *</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-0.5 w-full resize-y rounded-lg border border-gray-200 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800"
            />
          </label>

          <div className="flex gap-3">
            <label className="block flex-1">
              <span className="text-xs text-gray-500">Priorytet</span>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800"
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
            <label className="block flex-1">
              <span className="text-xs text-gray-500">Termin</span>
              <DatePicker
                value={deadline ? deadline.slice(0, 10) : ""}
                onChange={setDeadline}
                className="mt-0.5"
              />
            </label>
          </div>

          {lineItems.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">Pozycje ({lineItems.length}) — z propozycji ARON</span>
              <ul className="mt-1 space-y-0.5 rounded-lg border border-gray-100 p-2 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300">
                {lineItems.map((li, i) => (
                  <li key={i} className="truncate">
                    • {String(li.custom_name || li.description || li.line_item_type || "pozycja")}
                    {li.quantity != null ? ` — ${String(li.quantity)}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Anuluj
          </button>
          <button
            type="button"
            disabled={!valid || execute.isPending}
            onClick={submit}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {execute.isPending && <Loader2 size={14} className="animate-spin" />}
            Utwórz zapotrzebowanie
          </button>
        </div>
      </div>
    </div>
  );
}
